import React from 'react'
import { observer } from 'mobx-react'
import TeamMember from 'iam/models/TeamMember'
import Avatar from 'components/Avatar'
import Icon from 'components/Icon'
import Button from 'components/Button'
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from 'components/Table'
import styles from './MembersTable.m.sass'
import {
  Dropdown,
  DropdownItem,
  DropdownDivider,
  DropdownTitle,
  DropdownItemDescription,
  DropdownItemTitle,
} from 'components/Dropdown'
import TeamMembersViewModel, {
  teamMemberTableColumns,
} from '../TeamMembersViewModel'
import { Id } from 'types/definitions'
import TeamRole from 'iam/models/TeamRole'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  vm: TeamMembersViewModel
  teamMembers: TeamMember[]
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: '2-digit',
  year: 'numeric',
})

export default observer(function MembersTable({ vm }: Props) {
  const { filtered, currentSort, currentSortOrder, setSort } = vm
  return (
    <div>
      <Table striped stickyHeader>
        <TableHead>
          {teamMemberTableColumns.map((col) => (
            <TableHeader
              key={col.key}
              align="left"
              canSort={col.sortable}
              sortOrder={col.key === currentSort ? currentSortOrder : 'none'}
              onSort={(newSort) => setSort(col.key, newSort)}
            >
              {col.header}
            </TableHeader>
          ))}
          <TableHeader />
        </TableHead>
        <TableBody>
          {filtered.map((teamMember) => {
            const memberRole = teamMember.teamRole
            return (
              <TableRow key={Id.assert(teamMember.member.user.id)}>
                <TableCell>
                  <div className={styles.memberItem}>
                    <div>
                      <Avatar size={32} subject={teamMember.member} />
                    </div>
                    <div className={styles.itemInfo}>
                      <p className={styles.infoTitle}>
                        {teamMember.member.displayName}
                      </p>
                      <p className={styles.infoSubitle}>
                        {teamMember.member.email}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>{teamMember.teamRole.name}</TableCell>
                <TableCell width={200}>
                  {dateFormatter.format(teamMember.dateCreated)}
                </TableCell>
                <TableCell align="right" width={25}>
                  <TeamMemberActions
                    teamRoles={vm.teamRoles}
                    teamMember={teamMember}
                    memberRole={memberRole}
                    canManageMember={vm.canManageTeamMember}
                    onChangeTeamMemberRole={(teamMember, newRoleId) =>
                      vm.changeTeamMemberRole(teamMember, newRoleId)
                    }
                    onRemoveTeamMember={(teamMember) =>
                      vm.removeTeamMember(teamMember)
                    }
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
})

interface MemberActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  teamRoles: TeamRole[]
  teamMember: TeamMember
  memberRole: TeamRole
  canManageMember: boolean
  onChangeTeamMemberRole: (teamMember: TeamMember, newRoleId: string) => void
  onRemoveTeamMember: (teamMember: TeamMember) => void
}

const TeamMemberActions = observer(function ({
  teamRoles,
  teamMember,
  memberRole,
  canManageMember,
  onChangeTeamMemberRole,
  onRemoveTeamMember,
}: MemberActionsProps) {
  return (
    <Dropdown
      autoDismiss={true}
      position="bottom-end"
      button={
        <Button className={styles.memberActions} type="text">
          <Icon name="more" />
        </Button>
      }
    >
      <DropdownTitle>Change Role</DropdownTitle>
      {teamRoles.map((role) => {
        return (
          <DropdownItem
            onClick={() =>
              onChangeTeamMemberRole(teamMember, Id.assert(role.id))
            }
            disabled={role.name === memberRole.name || !canManageMember}
            active={role.name === memberRole.name}
            checkmark={role.name === memberRole.name}
            key={Id.assert(role.id)}
          >
            <DropdownItemTitle>{role.name}</DropdownItemTitle>
            <DropdownItemDescription>
              {role.description}
            </DropdownItemDescription>
          </DropdownItem>
        )
      })}
      {canManageMember && (
        <>
          <DropdownDivider />
          <DropdownItem onClick={() => onRemoveTeamMember(teamMember)}>
            <DropdownItemTitle>Remove Member</DropdownItemTitle>
            <DropdownItemDescription>
              Remove this user from this team
            </DropdownItemDescription>
          </DropdownItem>
        </>
      )}
    </Dropdown>
  )
})
